import { setNewTracking } from "Actions/IABudget";
import { useBudgetContext } from "Hooks/useBudgetContext";
import { Product } from "Hooks/useProducts/interface";
import {
  AddedProduct,
  IAResponse,
  NewTrackingModel,
  TrackingItem,
} from "Pages/PreBudgetConvertionIA/interface";
import { IAResult } from "Pages/PreBudgetConvertionIA/Provider";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Store } from "Reducers/interface";

export type ProductBeingReplaced =
  | {
      nome_original: string;
      nome_encontrado: string | null;
      id: number | null;
      encontrado: boolean;
      metodo: string | null;
      confianca: number;
      price: number | null;
    }
  | undefined;

interface SaveTracking {
  productBeingReplaced: ProductBeingReplaced;
  localSelectedProducts: Product[];
}

export const useIATracking = () => {
  const {
    setNewTrackingModel,
    trackingIAProducts,
    setTrackingProducts,
    newTrackingModel,
    budget,
  } = useBudgetContext();

  const dispatch = useDispatch();

  const { IAReponse, newTracking } = useSelector(
    (store: Store) => store.IABudget
  );

  const initializeNewTracking = useCallback((iaResponseData: IAResponse) => {
    if (
      !iaResponseData?.matching_results?.resultados ||
      !iaResponseData?.consolidated_analysis?.ai_analysis?.examType
    ) {
      return;
    }

    const { resultados } = iaResponseData.matching_results;
    const { examType } = iaResponseData.consolidated_analysis.ai_analysis;

    const exams_normalized = Array.isArray(examType) ? examType : [];

    const trackingItems: TrackingItem[] = resultados
      .filter((result: IAResult) => result.encontrado && result.id)
      .map((result: IAResult) => ({
        original_product: {
          original_name: result.nome_original,
          found_name: result.nome_encontrado || result.nome_original,
          id: result.id as number, // Garantir que não é null
          details: {
            method: result.metodo || "unknown",
            confidence: result.confianca || 0,
          },
        },
        final_status: "kept" as const,
      }));

    const not_founded = resultados
      .filter((result: IAResult) => !result.encontrado)
      .map((result: IAResult) => result.nome_original);

    const added_products: AddedProduct[] = [];

    const newTrackingModelData: NewTrackingModel = {
      exams_normalized,
      items: trackingItems,
      added_products,
      not_founded,
    };

    dispatch(setNewTracking(newTrackingModelData));
    setNewTrackingModel(newTrackingModelData);
  }, []);

  const saveTrackingIAProducts = ({
    localSelectedProducts,
    productBeingReplaced,
  }: SaveTracking) => {
    const tracking = [...trackingIAProducts];

    if (productBeingReplaced) {
      const productAreadyTracked = tracking.find(
        (track) =>
          track.produto_original.produto_original_match_id ===
          productBeingReplaced.id
      );

      if (productAreadyTracked) {
        const trackedItems = tracking.map((track) => {
          if (
            track.produto_original.produto_original_match_id ===
            productBeingReplaced.id
          ) {
            return {
              ...productAreadyTracked,
              items_corretos: [
                ...productAreadyTracked.items_corretos,
                ...localSelectedProducts.map((product) => ({
                  produto_corrigido_nome: product.product,
                  produto_corrigido_id: product.id,
                })),
              ],
            };
          }

          return track;
        });

        setTrackingProducts([...trackedItems]);

        return;
      }

      const trackItem = {
        produto_original: {
          produto_original_nome: productBeingReplaced.nome_original,
          produto_original_match_nome:
            productBeingReplaced.nome_encontrado || "",
          produto_original_match_id: productBeingReplaced.id || 0,
        },
        items_corretos: localSelectedProducts.map((product) => ({
          produto_corrigido_nome: product.product,
          produto_corrigido_id: product.id,
        })),
      };

      setTrackingProducts([...tracking, trackItem]);
    }
  };

  const addNonIAProductTracking = (product: Product) => {
    const oldTrackingList = newTrackingModel?.added_products || [];

    const newAddedProduct = {
      id: product.id,
      name: product.product,
    };

    const productExists = newTrackingModel?.added_products.some(
      (existingProduct) => existingProduct.id === product.id
    );

    if (productExists) return;

    if (newTrackingModel) {
      const updatedNewTrackingModel = {
        ...newTrackingModel,
        added_products: [...oldTrackingList, newAddedProduct],
      };

      dispatch(setNewTracking(updatedNewTrackingModel));

      setNewTrackingModel(updatedNewTrackingModel);
    }
  };

  const updateItemStatus = (
    originalProductId: number,
    status: "replaced" | "removed",
    finalProducts?: Product[]
  ) => {
    if (!newTrackingModel) return;

    const updatedList = {
      ...newTrackingModel,
      items: newTrackingModel?.items.map((item) => {
        if (item.original_product.id === originalProductId) {
          const updatedItem: TrackingItem = {
            ...item,
            final_status: status,
          };

          if (status === "replaced" && finalProducts) {
            updatedItem.final_products = finalProducts.map((product) => ({
              id: product.id,
              name: product.product,
            }));
          }

          return updatedItem;
        }
        return item;
      }),
    };

    dispatch(setNewTracking(updatedList));

    setNewTrackingModel(updatedList);
  };

  const replaceItemWithTracking = (
    originalProductId: number,
    newProducts: Product[]
  ) => {
    const originalItem = budget?.cart.find(
      (item) => item.product_id === originalProductId
    );

    const itemFoundedWithIA = IAReponse?.matching_results.resultados.find(
      (item) => item.id === originalProductId
    );

    if (!originalItem) return;

    if (originalItem.ai) {
      updateItemStatus(originalProductId, "replaced", newProducts);
    }

    if (originalItem.ai && itemFoundedWithIA) {
      const trackItem = {
        produto_original: {
          produto_original_nome: itemFoundedWithIA.nome_original,
          produto_original_match_nome: itemFoundedWithIA.nome_encontrado || "",
          produto_original_match_id: itemFoundedWithIA.id || 0,
        },
        items_corretos: newProducts.map((product) => ({
          produto_corrigido_nome: product.product,
          produto_corrigido_id: product.id,
        })),
      };

      setTrackingProducts([...trackingIAProducts, trackItem]);
    }
  };

  const deleteItemWithTracking = (productId: number) => {
    const itemToDelete = budget?.cart.find(
      (item) => item.product_id === productId
    );

    const itemFoundedWithIA = IAReponse?.matching_results.resultados.find(
      (item) => item.id === productId
    );

    if (itemToDelete) {
      if (itemToDelete.ai) {
        updateItemStatus(productId, "removed");
      }

      if (newTrackingModel && !itemToDelete.ai) {
        const updatedList = {
          ...newTrackingModel,
          added_products: newTrackingModel?.added_products.filter(
            (addedProduct) => addedProduct.id !== productId
          ),
        };

        dispatch(setNewTracking(updatedList));

        return setNewTrackingModel(updatedList);
      }

      if (itemToDelete.ai && itemFoundedWithIA) {
        const trackItem = {
          produto_original: {
            produto_original_nome: itemFoundedWithIA.nome_original,
            produto_original_match_nome:
              itemFoundedWithIA.nome_encontrado || "",
            produto_original_match_id: itemFoundedWithIA.id || 0,
          },
          items_corretos: [],
        };
        setTrackingProducts([...trackingIAProducts, trackItem]);
      }
    }
  };

  useEffect(() => {
    if (newTracking) setNewTrackingModel(newTracking);
  }, []);

  return {
    initializeNewTracking,
    saveTrackingIAProducts,
    deleteItemWithTracking,
    replaceItemWithTracking,
    addNonIAProductTracking,
    updateItemStatus,
  };
};
